import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell
} from 'recharts';
import type { MatchScores } from '../../api/types';

interface Props {
  scores: MatchScores;
  compact?: boolean;
}

const COLORS: Record<string, string> = {
  event: '#7c3aed',          // purple
  location: '#2563eb',       // blue
  time: '#059669',           // green
  appearance: '#d97706',     // amber
  mirrorConsistency: '#dc2626', // red
  identity: '#ec4899',       // pink (NEW)
  circle: '#14b8a6',         // teal (NEW)
  relationship: '#8b5cf6',   // violet (NEW)
};

export default function ScoreBreakdown({ scores, compact = false }: Props) {
  // Base dimensions (always shown)
  const baseDimensions = [
    { key: 'event', label: 'Event', value: scores.event },
    { key: 'location', label: 'Location', value: scores.location },
    { key: 'time', label: 'Time', value: scores.time },
    { key: 'appearance', label: 'Appearance', value: scores.appearance },
    { key: 'mirrorConsistency', label: 'Mirror', value: scores.mirrorConsistency },
  ];

  // Optional dimensions (only shown when present)
  const optionalDimensions: { key: string; label: string; value: number }[] = [];

  if (scores.identity != null) {
    optionalDimensions.push({ key: 'identity', label: 'Identity', value: scores.identity });
  }
  if (scores.circle != null) {
    optionalDimensions.push({ key: 'circle', label: 'Circle', value: scores.circle });
  }
  if (scores.relationship != null) {
    optionalDimensions.push({ key: 'relationship', label: 'Relationship', value: scores.relationship });
  }

  const dimensions = [...baseDimensions, ...optionalDimensions];

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {dimensions.map((d) => (
          <div
            key={d.key}
            className="flex items-center gap-1 text-xs"
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: COLORS[d.key] }}
            />
            <span className="text-gray-500">{d.label}:</span>
            <span className="font-medium">{(d.value * 100).toFixed(0)}%</span>
          </div>
        ))}
        {scores.llmJudge != null && (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-500">LLM:</span>
            <span className="font-medium">{(scores.llmJudge * 100).toFixed(0)}%</span>
          </div>
        )}
      </div>
    );
  }

  const radarData = dimensions.map((d) => ({
    dimension: d.label,
    value: d.value * 100,
  }));

  const barData = dimensions.map((d) => ({
    name: d.label,
    value: d.value * 100,
    color: COLORS[d.key],
  }));

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Radar Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
            <Radar
              name="Score"
              dataKey="value"
              stroke="#7c3aed"
              fill="#7c3aed"
              fillOpacity={0.3}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={barData} layout="vertical">
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {barData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="md:col-span-2 grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-900">
            {(scores.overall * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-gray-500">Deterministic</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-pebbl-600">
            {scores.llmJudge != null ? `${(scores.llmJudge * 100).toFixed(0)}%` : '-'}
          </div>
          <div className="text-xs text-gray-500">LLM Judge</div>
        </div>
        <div className="p-3 bg-pebbl-50 rounded-lg">
          <div className="text-2xl font-bold text-pebbl-700">
            {scores.final != null ? `${(scores.final * 100).toFixed(0)}%` : `${(scores.overall * 100).toFixed(0)}%`}
          </div>
          <div className="text-xs text-pebbl-500">Final Score</div>
        </div>
      </div>

      {/* Extended Score Indicators (when available) */}
      {optionalDimensions.length > 0 && (
        <div className="md:col-span-2 border-t pt-4">
          <div className="text-xs text-gray-500 mb-2">Extended Matching Scores</div>
          <div className="flex flex-wrap gap-3">
            {optionalDimensions.map((d) => (
              <div
                key={d.key}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS[d.key] }}
                />
                <span className="text-sm text-gray-700">{d.label}</span>
                <span className="text-sm font-semibold">{(d.value * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LLM dimension scorer reasoning (in-memory only on fresh matches) */}
      {(scores.locationReasoning || scores.timeReasoning) && (
        <div className="md:col-span-2 border-t pt-4 space-y-2">
          <div className="text-xs text-gray-500 mb-1">Scorer Reasoning</div>
          {scores.locationReasoning && (
            <div className="text-sm bg-blue-50 border border-blue-100 rounded-md p-2">
              <span className="font-medium text-blue-700">Location:</span>{' '}
              <span className="text-gray-700">{scores.locationReasoning}</span>
            </div>
          )}
          {scores.timeReasoning && (
            <div className="text-sm bg-green-50 border border-green-100 rounded-md p-2">
              <span className="font-medium text-green-700">Time:</span>{' '}
              <span className="text-gray-700">{scores.timeReasoning}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
